package com.ives.modules.measurements;

import com.ives.modules.mpr.MPR;
import Lambda;
import haxe.Timer;
import com.ives.modules.cine.Cine;
import com.ives.components.notifier.Notifier;
import js.Browser;
import js.html.KeyboardEvent;
import js.html.TouchEvent;
import js.html.svg.PathElement;
import js.html.svg.GElement;
import js.html.svg.EllipseElement;
import js.html.Element;
import js.html.MouseEvent;
import js.html.svg.SVGElement;

import haxe.ds.Vector;
import haxe.Timer;
import haxe.ds.StringMap;

import com.ives.utils.math.GeometryUtils;
import com.ives.utils.math.Vector2D;
import com.ives.utils.math.Vector3D;
import com.ives.utils.Translator;
import com.ives.utils.math.MathStats;
import com.ives.utils.Log;
import com.ives.components.workspace.DicomWorkspace;
import com.ives.components.viewer.DicomViewer;
import com.ives.components.messagehandler.MessageHandler;
import com.ives.utils.DicomUtils;
import com.ives.components.measpopup.MeasurementPopup;

class Measurements extends Module{
	static public inline var MEASUREMENTS_MODULE:String = "MEASUREMENTS_MODULE";
	static public inline var MEASUREMENTS_RESET:String = "MEASUREMENTS_RESET";

	static public inline var MEASUREMENTS:String = "MEASUREMENTS";
	static public inline var MEASUREMENTS_POINT:String = "MEASUREMENTS_POINT";
	static public inline var MEASUREMENTS_LINE:String = "MEASUREMENTS_LINE";
	static public inline var MEASUREMENTS_PROFILE:String = "MEASUREMENTS_PROFILE";
	static public inline var MEASUREMENTS_RECTANGLE:String = "MEASUREMENTS_RECTANGLE";
	static public inline var MEASUREMENTS_ELLIPSIS:String = "MEASUREMENTS_ELLIPSIS";
	static public inline var MEASUREMENTS_SHAPE:String = "MEASUREMENTS_SHAPE";
	static public inline var MEASUREMENTS_ANGLE:String = "MEASUREMENTS_ANGLE";
	static public inline var MEASUREMENTS_COBB:String = "MEASUREMENTS_COBB";
	static public inline var MEASUREMENTS_RULERS:String = "MEASUREMENTS_RULERS";
	static public inline var MEASUREMENTS_PROPORTION:String = "MEASUREMENTS_PROPORTION";
	static public inline var MEASUREMENTS_DELETE:String = "MEASUREMENTS_DELETE";
	static public inline var MEASUREMENTS_EXTERNAL:String = "MEASUREMENTS_EXTERNAL";

	static public inline var MEASUREMENTS_SHAPE_CLICK:String = "MEASUREMENTS_SHAPE_CLICK";

	static private inline var PROFILE_DRAWING_ID:String = "P";
	static private inline var PROFILE_DRAWING_COLOR:String = "#ff0000";

	static private inline var DELETE_KEY:String = "delete";

	private var pointers:Array<Pointer> = [];
	private var rulersShape:SVGElement;
	private var ellipsisShape:SVGElement;

	private var points:Array<Float> = [];

	private var histogramStart:Vector2D;
	private var histogramStop:Vector2D;
	private var histogramEvent:ModuleEvent;

	private var enableRulers:Bool = false;
	private var isDown:Bool = false;

	private var timer:Timer;
	private var mtimer:Timer;

	private var lastTouch:Vector3D;
	private var lastTap:Float = 0;

	private var currentDrawingId:String = "";

	override public function init(dicomViewer:DicomViewer, ?dicomWorkspace:DicomWorkspace){
		super.init(dicomViewer, dicomWorkspace);

		dicomWorkspace.on("mousedown", onMouseDown);
		dicomWorkspace.on("mousemove", onMouseMove);
		dicomWorkspace.on("mouseup", onMouseUp);
		dicomWorkspace.on("mouseout", onMouseOut);

		dicomWorkspace.on("touchstart", onTouchStart);
		dicomWorkspace.on("touchmove", onTouchMove);
		dicomWorkspace.on("touchend", onTouchEnd);
		dicomWorkspace.on("touchleave", onMouseOut);

		dicomWorkspace.on("dblclick", onDoubleClick);

		Browser.window.addEventListener("keyup", onKeyUp);

		on(histogramContainer, "mousemove", onOverHistogram);

		Timer.delay(function(){
			Log.info('Lazy measuremnts init of size ${Lambda.count(MessageHandler.externalMeasurements)}');

			for(measurementData in MessageHandler.externalMeasurements.iterator())
				dispatch(MessageHandler.VIEWER, new ModuleEvent(MEASUREMENTS_EXTERNAL, measurementData.points, [measurementData.imageId, measurementData.measurementType, measurementData.drawingId]));
		}, 2000);
	}

	private function measurementPopup(event:ModuleEvent, values:Array<String>, delay:Int = 1000){
		if(Browser.window.location.search.indexOf("measurementPopup") == -1)
			return;

		if(mtimer != null)
			mtimer.stop();

		mtimer = Timer.delay(function(){
			new MeasurementPopup(event.type, values).open();
		}, delay);
	}

	override public function handleModuleEvent(event:ModuleEvent){
		if(event.type == MEASUREMENTS_EXTERNAL){
			var imageId:String = event.strings[0];
			var measurementType:String = event.strings[1];
			var drawingId:String = event.strings[2];

			if(imageId == dicomWorkspace.dicomImageData.id){
				Log.debug("Received external measurement for this workspace");

				event = new ModuleEvent(measurementType, event.points, [drawingId], dicomWorkspace.id, true, false);
			}
		}

		if(event.points != null){
			hideHistogram();

			if(event.type.indexOf(MEASUREMENTS) != -1)
				hide(ellipse);
		}

		if(dicomWorkspace.dicomImage.position == null) // do not show on non-full res images - they dont have exact pixel data
			return;

		if(event.type == MEASUREMENTS_PROFILE || event.type == MEASUREMENTS_LINE){
			Log.debug('Line!');

			var drawing:Drawing = new Drawing(Drawing.SHAPE, event.points, event.strings[0], !event.editable, false, false, onModified, event);

			addDrawing(drawing);

			if(event.type == MEASUREMENTS_PROFILE){
				histogramEvent = event;

				if(event.editable)
					histogramContainer.classList.add("mouseEnabled");

				updateHistogram(volumeToImagePoint(new Vector3D(event.points[0], event.points[1], event.points[2])), volumeToImagePoint(new Vector3D(event.points[3], event.points[4], event.points[5])), event.scalar);
			}

			var vectors:Array<Vector3D> = floatsToPoints3D(event.points);
			var length:Float = vectors[1].subtract(vectors[0]).length() * 1;
			drawing.measurementInfo.lengthValue.innerHTML = MathStats.formatLengthInMm(length);

			showComponents([drawing.measurementInfo.lengthBox], drawing.measurementInfo);

			measurementPopup(event, [
				drawing.measurementInfo.lengthValue.innerHTML,
			]);

			sendMeasurementData(event);
		}

		if(event.type == MEASUREMENTS_RECTANGLE || event.type == MEASUREMENTS_ELLIPSIS){
			Log.debug('Rectangle ${event.type}}!');

			var drawing:Drawing = new Drawing(Drawing.SHAPE, event.points, event.strings[0], !event.editable, true, true, onModified, event);

			addDrawing(drawing);

			if(event.type == MEASUREMENTS_ELLIPSIS){
				var localPoints:Array<Vector2D> = volumeToLocal(floatsToPoints3D(event.points));

				var ellipse:EllipseElement = cast this.ellipse;
				var ellipseTransform:GElement = cast this.ellipseTransform;

				show(this.ellipse); // FIXME for multiple measurements

				var radians = Math.atan2(localPoints[0].y - localPoints[1].y, localPoints[0].x - localPoints[1].x) - Math.atan2(localPoints[2].y - localPoints[1].y, localPoints[2].x - localPoints[1].x);
				var deg = 180 * radians / Math.PI;

				if(deg > 180)
					deg -= 360;

				if(deg < -180)
					deg += 360;

				var transform:String = "";
				var rotation:Float;

				if(deg > 0){
					ellipse.cx.baseVal.value = ellipse.rx.baseVal.value = localPoints[0].subtract(localPoints[1]).length() / 2;
					ellipse.cy.baseVal.value = ellipse.ry.baseVal.value = localPoints[1].subtract(localPoints[2]).length() / 2;

					rotation = 0;
				}
				else{
					ellipse.cx.baseVal.value = ellipse.rx.baseVal.value = localPoints[1].subtract(localPoints[2]).length() / 2;
					ellipse.cy.baseVal.value = ellipse.ry.baseVal.value = localPoints[0].subtract(localPoints[1]).length() / 2;

					rotation = -90;
				}

				rotation += 180 * Math.atan2(localPoints[1].y - localPoints[0].y, localPoints[1].x - localPoints[0].x) / Math.PI;

				transform += 'translate(${localPoints[0].x}, ${localPoints[0].y}) rotate(${rotation})';

				ellipseTransform.setAttribute("transform", transform);

				radians = Math.atan2(event.points[1] - event.points[3], event.points[0] -  event.points[2]) - Math.atan2(0, 100);
				deg = 180 * radians / Math.PI;

				if(deg > 180)
					deg -= 360;
			}

			var vectors:Array<Vector3D> = floatsToPoints3D(event.points);

			var width:Float = vectors[0].subtract(vectors[1]).length() * 1;
			var height:Float = vectors[1].subtract(vectors[2]).length() * 1;
			var area:Float = width * height;
			var circumference:Float = width + width + height + height;

			if(event.type == MEASUREMENTS_ELLIPSIS){
				area *= Math.PI / 4;

				var a:Float = width / 2;
				var b:Float = height / 2;

				circumference = Math.PI * (1.5 * (a + b) - Math.sqrt(a * b));
			}

			drawing.measurementInfo.widthValue.innerHTML = MathStats.formatLengthInMm(width);
			drawing.measurementInfo.heightValue.innerHTML = MathStats.formatLengthInMm(height);
			drawing.measurementInfo.areaValue.innerHTML = MathStats.formatAreaInMm2(area);
			drawing.measurementInfo.circumferenceValue.innerHTML = MathStats.formatLengthInMm(circumference);

			showComponents([drawing.measurementInfo.widthBox, drawing.measurementInfo.widthBox, drawing.measurementInfo.areaBox, drawing.measurementInfo.circumferenceBox], drawing.measurementInfo);

			if(timer != null)
				timer.stop();

			timer = Timer.delay(function(){
				if(dicomWorkspace.dicomImage.pixels == null) // do not show on non-full res images - they dont have exact pixel data
					return;

				var localPoints = pointsToFloats2D(volumeToImage(floatsToPoints3D(event.points)));

				var minX:UInt = Std.int(MathStats.min(localPoints));
				var minY:UInt = Std.int(MathStats.min(localPoints, 1));
				var maxX:UInt = Std.int(MathStats.max(localPoints));
				var maxY:UInt = Std.int(MathStats.max(localPoints, 1));

				var intensities:Vector<Float> = dicomWorkspace.dicomImage.getIntensities(minX, minY, maxX, maxY);

				var inside:Array<Float> = [];

				var intensitiesWidth:UInt = maxX - minX;

				var localVectors:Array<Vector2D> = volumeToImage(floatsToPoints3D(event.points));
				var localWidth:Float = localVectors[0].subtract(localVectors[1]).length() * 1;
				var localHeight:Float = localVectors[1].subtract(localVectors[2]).length() * 1;

				var center:Vector2D = localVectors[2].subtract(localVectors[0]);
				center.scale(0.5);
				center.increment(localVectors[0]);

				for(x in Std.int(minX)...Std.int(maxX))
					for(y in minY...maxY){
						if(event.type == MEASUREMENTS_ELLIPSIS && MathStats.pointInEllipsis(x, y, center.x, center.y, localWidth/2, localHeight/2))
							inside.push((y - minY) * intensitiesWidth + (x - minX));

						if(event.type == MEASUREMENTS_RECTANGLE && MathStats.pointInPoly(x, y, localVectors))
							inside.push((y - minY) * intensitiesWidth + (x - minX));
					}

				var insideValues:Array<Float> = [];

				for(i in 0...inside.length)
					if(inside[i] >= 0 && inside[i] < intensities.length)
						insideValues.push(intensities[i]);

				drawing.measurementInfo.averageValue.innerHTML = '${MathStats.roundTo(MathStats.average(insideValues),1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';
				drawing.measurementInfo.medianValue.innerHTML = '${MathStats.roundTo(MathStats.median(insideValues), 1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';

				showComponents([drawing.measurementInfo.widthBox, drawing.measurementInfo.widthBox, drawing.measurementInfo.areaBox, drawing.measurementInfo.circumferenceBox, drawing.measurementInfo.averageBox, drawing.measurementInfo.medianBox], drawing.measurementInfo);

				measurementPopup(event, [
					drawing.measurementInfo.widthValue.innerHTML,
					drawing.measurementInfo.heightValue.innerHTML,
					drawing.measurementInfo.areaValue.innerHTML,
					drawing.measurementInfo.circumferenceValue.innerHTML,
					drawing.measurementInfo.averageValue.innerHTML,
					drawing.measurementInfo.medianValue.innerHTML,
				], 100);
			}, 1000);

			sendMeasurementData(event);
		}

		if(event.type == MEASUREMENTS_SHAPE){
			Log.debug('Shape!');

			var drawing:Drawing = new Drawing(Drawing.SHAPE, event.points, event.strings[0], !event.editable, true, true, onModified, event);

			addDrawing(drawing);

			var slicePoints = pointsToFloats2D(dicomWorkspace.dicomImage.volumeToSliceCoordinates(floatsToPoints3D(event.points)));

			var minX:UInt = Std.int(MathStats.min(slicePoints));
			var minY:UInt = Std.int(MathStats.min(slicePoints, 1));
			var maxX:UInt = Std.int(MathStats.max(slicePoints));
			var maxY:UInt = Std.int(MathStats.max(slicePoints, 1));

			var width:Float = (maxX - minX) * 1;
			var height:Float = (maxY - minY) * 1;

			var vectors:Array<Vector3D> = floatsToPoints3D(event.points);

			var sum:Float = 0;

			for(n in 0...vectors.length)
				sum += vectors[n].subtract(vectors[(n + 1) % vectors.length]).length();

			var circumference:Float = sum * 1;

			var area:Float = MathStats.polygonArea(slicePoints);

			drawing.measurementInfo.widthValue.innerHTML = MathStats.formatLengthInMm(width);
			drawing.measurementInfo.heightValue.innerHTML = MathStats.formatLengthInMm(height);
			drawing.measurementInfo.areaValue.innerHTML = MathStats.formatAreaInMm2(area);
			drawing.measurementInfo.circumferenceValue.innerHTML = MathStats.formatLengthInMm(circumference);

			showComponents([drawing.measurementInfo.widthBox, drawing.measurementInfo.widthBox, drawing.measurementInfo.areaBox, drawing.measurementInfo.circumferenceBox], drawing.measurementInfo);

			if(MathStats.polygonIntersects(slicePoints))
				drawing.measurementInfo.areaValue.innerHTML = Translator.getTransltaion("error");

			if(timer != null)
				timer.stop();

			timer = Timer.delay(function(){
				if(dicomWorkspace.dicomImage.pixels == null) // do not show on non-full res images - they dont have exact pixel data
					return;

				var localPoints = pointsToFloats2D(volumeToImage(floatsToPoints3D(event.points)));

				var minX:UInt = Std.int(MathStats.min(localPoints));
				var minY:UInt = Std.int(MathStats.min(localPoints, 1));
				var maxX:UInt = Std.int(MathStats.max(localPoints));
				var maxY:UInt = Std.int(MathStats.max(localPoints, 1));

				var inside:Array<Float> = [];
				var intensitiesWidth:UInt = maxX - minX;
				var intensities:Vector<Float> = dicomWorkspace.dicomImage.getIntensities(minX, minY, maxX, maxY);

				var localVectors:Array<Vector2D> = floatsToPoints2D(localPoints);

				for(x in minX...maxX)
					for(y in minY...maxY)
						if(MathStats.pointInPoly(x, y, localVectors))
							inside.push((y - minY) * intensitiesWidth + (x - minX));

				var insideValues:Array<Float> = [];

				for(i in 0...inside.length)
					if(inside[i] >= 0 && inside[i] < intensities.length)
						insideValues.push(intensities[i]);

				drawing.measurementInfo.averageValue.innerHTML = '${MathStats.roundTo(MathStats.average(insideValues),1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';
				drawing.measurementInfo.medianValue.innerHTML = '${MathStats.roundTo(MathStats.median(insideValues), 1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';

				showComponents([drawing.measurementInfo.widthBox, drawing.measurementInfo.widthBox, drawing.measurementInfo.areaBox, drawing.measurementInfo.circumferenceBox, drawing.measurementInfo.averageBox, drawing.measurementInfo.medianBox], drawing.measurementInfo);

				measurementPopup(event, [
					drawing.measurementInfo.widthValue.innerHTML,
					drawing.measurementInfo.heightValue.innerHTML,
					drawing.measurementInfo.areaValue.innerHTML,
					drawing.measurementInfo.circumferenceValue.innerHTML,
					drawing.measurementInfo.averageValue.innerHTML,
					drawing.measurementInfo.medianValue.innerHTML,
				], 100);
			}, 1000);

			sendMeasurementData(event);
		}

		if(event.type == MEASUREMENTS_ANGLE){
			Log.debug('Angle');

			var drawing:Drawing = new Drawing(Drawing.SHAPE, event.points, event.strings[0], !event.editable, onModified, event);

			addDrawing(drawing);
			showComponents([], drawing.measurementInfo);

			var localPoints:Array<Vector2D> = volumeToLocal(floatsToPoints3D(event.points));

			if(localPoints.length == 3){
				var radians = Math.atan2(localPoints[0].y - localPoints[1].y, localPoints[0].x - localPoints[1].x) - Math.atan2(localPoints[2].y - localPoints[1].y, localPoints[2].x - localPoints[1].x);
				var angle = 180 * Math.abs(radians) / Math.PI;

				angle = angle % 360;
				angle = angle > 180 ? 360 - angle : angle;

				var rest:Float = 180 - angle;

				drawing.measurementInfo.angleValue.innerHTML = '${MathStats.roundTo(angle,2)}° (${MathStats.roundTo(rest,2)}°)';

				showComponents([drawing.measurementInfo.angleBox], drawing.measurementInfo);

				measurementPopup(event, [
					drawing.measurementInfo.angleValue.innerHTML,
				]);
			}

			sendMeasurementData(event);
		}

		if(event.type == MEASUREMENTS_COBB || event.type == MEASUREMENTS_PROPORTION){
			Log.debug('Cobb or prop ${event.type}');

			var shape:Array<Float> = [
				event.points[0], event.points[1], event.points[2],
				event.points[3], event.points[4], event.points[5],
				(event.points[0] + event.points[3]) / 2, (event.points[1] + event.points[4]) / 2, (event.points[2] + event.points[5]) / 2
			];

			if(event.points.length == 9){
				shape.push(event.points[6]);
				shape.push(event.points[7]);
				shape.push(event.points[8]);
			}

			if(event.points.length == 12){
				shape.push((event.points[6] + event.points[9]) / 2);
				shape.push((event.points[7] + event.points[10]) / 2);
				shape.push((event.points[8] + event.points[11]) / 2);
				shape.push(event.points[6]);
				shape.push(event.points[7]);
				shape.push(event.points[8]);
				shape.push(event.points[9]);
				shape.push(event.points[10]);
				shape.push(event.points[11]);
			}

			var drawing:Drawing = new Drawing(Drawing.SHAPE, shape, event.points, event.strings[0], !event.editable, onModified, event);

			addDrawing(drawing);

			showComponents([], drawing.measurementInfo);

			var localPoints:Array<Vector2D> = volumeToLocal(floatsToPoints3D(event.points));

			if(localPoints.length == 4){
				if(event.type == MEASUREMENTS_COBB){
					var radians = Math.atan2(localPoints[0].y - localPoints[1].y, localPoints[0].x - localPoints[1].x) - Math.atan2(localPoints[3].y - localPoints[2].y, localPoints[3].x - localPoints[2].x);
					var angle = 180 * Math.abs(radians) / Math.PI;

					angle = angle % 360;
					angle = angle > 180 ? 360 - angle : angle;
					angle = angle > 90 ? 180 - angle : angle;

					var rest:Float = 180 - angle;

					drawing.measurementInfo.angleValue.innerHTML = '${MathStats.roundTo(angle,2)}° (${MathStats.roundTo(rest,2)}°)';

					showComponents([drawing.measurementInfo.angleBox], drawing.measurementInfo);

					measurementPopup(event, [
						drawing.measurementInfo.angleValue.innerHTML,
					]);
				}
				else{
					var proportion:Float = localPoints[1].subtract(localPoints[0]).length() / localPoints[3].subtract(localPoints[2]).length();
					var proportionInverted = 1 / proportion;

					drawing.measurementInfo.proportionValue.innerHTML = '${MathStats.roundTo(proportion,2)} (${MathStats.roundTo(proportionInverted,2)})';

					showComponents([drawing.measurementInfo.proportionBox], drawing.measurementInfo);

					measurementPopup(event, [
						drawing.measurementInfo.proportionValue.innerHTML,
					]);
				}
			}

			sendMeasurementData(event);
		}

		if(event.type == MEASUREMENTS_POINT){
			if(event.points != null){
				var imagePoint = dicomWorkspace.dicomImage.getImageCoordinates(new Vector3D(event.points[0], event.points[1], event.points[2]));

				var value:Float = dicomWorkspace.dicomImage.getPixel(Std.int(imagePoint.x), Std.int(imagePoint.y));

				point(event.points[0], event.points[1], event.points[2], event.strings[0], event.editable, '${MathStats.roundTo(value,1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}');
			}
			else
				stopPointing(event.strings[0]);
		}

		if(event.type == MEASUREMENTS_RULERS){
			Log.debug('Rulers');

			enableRulers = !enableRulers;

			updateRulers();
		}

		if(event.type == DicomViewer.RESIZED){
			Log.debug('Redraw!');

			redraw();
			updateRulers();

//			for(drawing in this.drawings.iterator())
//				if(drawing.event.type == MEASUREMENTS_ELLIPSIS)
//					handleModuleEvent(drawing.event);
		}

		if(event.type == DicomViewer.RESIZE)
			updateRulers();

		if(event.type == MEASUREMENTS_DELETE){
			Log.debug("Measurements delete!");

			for(drawing in drawings.iterator())
				if(drawing.event.type.indexOf(MEASUREMENTS) != -1)
					MessageHandler.send(new MeasurementData(MEASUREMENTS_DELETE, dicomWorkspace.dicomImageData.id, [], drawing.id));

			MessageHandler.externalMeasurements = new StringMap<MeasurementData>();

			clearCanvas();
		}

		if(event.type == MEASUREMENTS_SHAPE_CLICK)
			Notifier.info('<span data-tr="measurements.deleteNotification"></span> <kbd>Delete</kbd>');
	}

	override private function redraw(){
		hide(ellipse);

		super.redraw();
	}

	private function sendMeasurementData(event:ModuleEvent){
		if(event.editable == true){
			var measurementData:MeasurementData = new MeasurementData(event.type, dicomWorkspace.dicomImageData.id, event.points, event.strings[0]);

			MessageHandler.externalMeasurements.set(measurementData.drawingId, measurementData);

			MessageHandler.send(measurementData);
		}
	}

	private function onModified(drawing:Drawing){
		Log.debug('Modified!');

		drawing.event.sync = true;
		drawing.event.external = false;

		if(drawing.event.type == MEASUREMENTS_ELLIPSIS || drawing.event.type == MEASUREMENTS_RECTANGLE){
			drawing.event.points[drawing.lastHandleIndex] = drawing.handlePoints[drawing.lastHandleIndex];
			drawing.event.points[drawing.lastHandleIndex + 1] = drawing.handlePoints[drawing.lastHandleIndex + 1];
			drawing.event.points[drawing.lastHandleIndex + 2] = drawing.handlePoints[drawing.lastHandleIndex + 2];

			var vector:Array<Vector2D> = volumeToLocal(floatsToPoints3D(drawing.event.points));
			var localPoints = pointsToFloats2D(vector);

			var backup:Array<Float> = drawing.points.copy();

			if(drawing.lastHandleIndex == 0){
				var diff:Vector2D = GeometryUtils.vectorToPointCastToVector(vector[0], vector[1], vector[2]);

				localPoints[2] += diff.x;
				localPoints[3] += diff.y;

				diff = GeometryUtils.vectorToPointCastToVector(vector[0], vector[3], vector[2]);

				localPoints[6] += diff.x;
				localPoints[7] += diff.y;
			}

			if(drawing.lastHandleIndex == 3){
				var diff:Vector2D = GeometryUtils.vectorToPointCastToVector(vector[1], vector[0], vector[3]);

				localPoints[0] += diff.x;
				localPoints[1] += diff.y;

				diff = GeometryUtils.vectorToPointCastToVector(vector[1], vector[2], vector[3]);

				localPoints[4] += diff.x;
				localPoints[5] += diff.y;
			}

			if(drawing.lastHandleIndex == 6){
				var diff:Vector2D = GeometryUtils.vectorToPointCastToVector(vector[2], vector[1], vector[0]);

				localPoints[2] += diff.x;
				localPoints[3] += diff.y;

				diff = GeometryUtils.vectorToPointCastToVector(vector[2], vector[3], vector[0]);

				localPoints[6] += diff.x;
				localPoints[7] += diff.y;
			}

			if(drawing.lastHandleIndex == 9){
				var diff:Vector2D = GeometryUtils.vectorToPointCastToVector(vector[3], vector[0], vector[1]);

				localPoints[0] += diff.x;
				localPoints[1] += diff.y;

				diff = GeometryUtils.vectorToPointCastToVector(vector[3], vector[2], vector[1]);

				localPoints[4] += diff.x;
				localPoints[5] += diff.y;
			}

			vector = floatsToPoints2D(drawing.points);

			if(vector[0].subtract(vector[1]).length2() < 1 ||
			vector[0].subtract(vector[2]).length2() < 1 ||
			vector[0].subtract(vector[3]).length2() < 1 ||
			vector[1].subtract(vector[2]).length2() < 1 ||
			vector[1].subtract(vector[3]).length2() < 1 ||
			vector[2].subtract(vector[3]).length2() < 1){
				Log.debug('Change to small! Ignore....');

				drawing.points = backup;
				drawing.event.points = backup;

				drawing.handlePoints[drawing.lastHandleIndex] = drawing.lastHandlePosition[0];
				drawing.handlePoints[drawing.lastHandleIndex + 1] = drawing.lastHandlePosition[1];
				drawing.handlePoints[drawing.lastHandleIndex + 2] = drawing.lastHandlePosition[2];

				return;
			}

			drawing.points = drawing.event.points = pointsToFloats3D(localToVolume(floatsToPoints2D(localPoints)));
		}

		dispatchEvent(drawing.event);
	}

	private function lineRelated():String{
		if(dicomViewer.lineButton.checked)
			return MEASUREMENTS_LINE;

		if(dicomViewer.sectionButton.checked)
			return MEASUREMENTS_PROFILE;

		if(dicomViewer.areaButton.checked)
			return MEASUREMENTS_RECTANGLE;

		if(dicomViewer.ellipsisButton.checked)
			return MEASUREMENTS_ELLIPSIS;

		return null;
	}

	private function point(x:Float, y:Float, z:Float, id:String = "", updateOnly:Bool = false, value:String = ""){
		for(pointer in pointers){
			if(pointer.id == id){
				pointer.setImagePosition(x, y, z);
				pointer.setValue(value);
				updatePointers();

				return;
			}
		}

		if(updateOnly)
			return;

		var pointer:Pointer = new Pointer(id, new Vector3D(x, y, z));

		pointers.push(pointer);
		pointersContainer.appendChild(pointer.viewElement);

		updatePointers();
	}

	private function stopPointing(id:String = ""){
		for(pointer in pointers){
			if(pointer.id == id){
				pointer.remove();
				pointers.remove(pointer);

				return;
			}
		}
	}

	private function updatePointers(){
		for(pointer in pointers)
			pointer.setLocalPosition(volumeToLocalPoint(pointer.image));
	}

	private function onTouchStart(event:TouchEvent){
		if(!this.measurementsOn())
			return;

		event.preventDefault();

		lastTouch = touchToVolume(event);

		onDown(lastTouch);
	}

	private function onMouseDown(event:MouseEvent){
		if(hasModifier(event))
			return;

		onDown(mouseToVolume(event));
	}

	private function onDown(image:Vector3D){
		if(!this.measurementsOn())
			return;

		if(onDrawingMouseDown(image.x, image.y, image.z))
			return;

		isDown = true;

		if(points.length == 0){
			currentDrawingId = drawingId("shape");

			Log.debug('Starting new drawing ${currentDrawingId}');
		}

		if(dicomViewer.pointButton.checked)
			dispatchEvent(new ModuleEvent(MEASUREMENTS_POINT, [image.x, image.y, image.z], [MessageHandler.userName], dicomWorkspace.id, false, true));
	}

	private function onMouseOut(){
		if(points.length > 0){
			points = [];
			drawings.remove(currentDrawingId);
			redraw();
		}
	}

	private function clearCanvas(){
		points = [];

		clear();
		hideHistogram();

		if(timer != null)
			timer.stop();

		drawings = new StringMap<Drawing>();

		redraw();
		hide(ellipse);
	}


	private function onTouchMove(event:TouchEvent){
		if(!this.measurementsOn())
			return;

		lastTouch = touchToVolume(event);

		onMove(lastTouch);
	}

    private function measurementsOn() {
        return dicomViewer.pointButton.checked ||
        dicomViewer.lineButton.checked ||
        dicomViewer.sectionButton.checked ||
        dicomViewer.areaButton.checked ||
        dicomViewer.ellipsisButton.checked ||
        dicomViewer.shapeButton.checked ||
        dicomViewer.angleButton.checked ||
        dicomViewer.cobbButton.checked ||
        dicomViewer.proportionButton.checked;
    }

	private function onMouseMove(event:MouseEvent){
		if(hasModifier(event))
			return;

		if(!this.measurementsOn())
			return;

		if(dicomWorkspace.mode == MPR.MPR_WORKSPACE_H)
			return;

		onMove(mouseToVolume(event));
	}

	private function onMove(image:Vector3D){
		if(!this.measurementsOn())
			return;

		if(onDrawingMouseMove(image.x, image.y, image.z))
			return;

		if(dicomViewer.pointButton.checked && isDown)
			dispatchEvent(new ModuleEvent(MEASUREMENTS_POINT, [image.x, image.y, image.z], [MessageHandler.userName], dicomWorkspace.id, true, true));

		if(points.length == 0)
			return;

		var temp:Array<Float> = points.copy();

		temp.push(image.x);
		temp.push(image.y);
		temp.push(image.z);

		tryLineRelated(temp, false);

		if(dicomViewer.shapeButton.checked)
			if(temp.length >= 6)
				dispatchEvent(new ModuleEvent(MEASUREMENTS_SHAPE, temp, [currentDrawingId], dicomWorkspace.id, false, true));

		if(dicomViewer.angleButton.checked)
			if(temp.length >= 6)
				dispatchEvent(new ModuleEvent(MEASUREMENTS_ANGLE, temp, [currentDrawingId], dicomWorkspace.id, false, true));

		if(dicomViewer.cobbButton.checked)
			if(temp.length >= 6)
				dispatchEvent(new ModuleEvent(MEASUREMENTS_COBB, temp, [currentDrawingId], dicomWorkspace.id, false, true));

		if(dicomViewer.proportionButton.checked)
			if(temp.length >= 6)
				dispatchEvent(new ModuleEvent(MEASUREMENTS_PROPORTION, temp, [currentDrawingId], dicomWorkspace.id, false, true));
	}

	private function onTouchEnd(event:TouchEvent){
		if(!this.measurementsOn())
			return;

		var now:Float = Timer.stamp();

		if(now - lastTap < 0.5){
			onDouble(true);

			lastTap = now;

			return;
		}

		lastTap = now;

		onUp(lastTouch);
	}

	private function onMouseUp(event:MouseEvent){
		if(hasModifier(event))
			return;

		onUp(mouseToVolume(event));
	}

	private function onUp(image:Vector3D){
		if(!this.measurementsOn())
			return;

		if(onDrawingMouseUp(image.x, image.y, image.z))
			return;

		isDown = false;

		if(dicomViewer.pointButton.checked)
			dispatchEvent(new ModuleEvent(MEASUREMENTS_POINT, [MessageHandler.userName], dicomWorkspace.id, false, true));

		if(lineRelated() != null)
			if(points.length >= 6)
				points = [];

		if(dicomViewer.angleButton.checked)
			if(points.length >= 9)
				points = [];

		points.push(image.x);
		points.push(image.y);
		points.push(image.z);

		if(tryLineRelated(points.copy(), true))
			points = [];

		if(dicomViewer.shapeButton.checked)
			if(points.length >= 6)
				dispatchEvent(new ModuleEvent(MEASUREMENTS_SHAPE, points, [currentDrawingId], dicomWorkspace.id, false, true));

		if(dicomViewer.angleButton.checked)
			if(points.length >= 6){
				dispatchEvent(new ModuleEvent(MEASUREMENTS_ANGLE, points, [currentDrawingId], dicomWorkspace.id, (points.length == 9), true));

				if(points.length == 9)
					points = [];
			}

		if(dicomViewer.cobbButton.checked)
			if(points.length >= 9){
				dispatchEvent(new ModuleEvent(MEASUREMENTS_COBB, points, [currentDrawingId], dicomWorkspace.id, (points.length == 9), true));

				if(points.length == 12)
					points = [];
			}

		if(dicomViewer.proportionButton.checked)
			if(points.length >= 9){
				dispatchEvent(new ModuleEvent(MEASUREMENTS_PROPORTION, points, [currentDrawingId], dicomWorkspace.id, (points.length == 9), true));

				if(points.length == 12)
					points = [];
			}
	}

	private function onKeyUp(event:KeyboardEvent){
		Log.debug('Key pressed ${event.key.toLowerCase()}');

		if(event.key.toLowerCase() == DELETE_KEY)
			onDouble(false);
	}

	private function onDoubleClick(){
		onDouble(false);
	}

	private function onDouble(touch:Bool){
		if(!this.measurementsOn())
			return;

		if(dicomViewer.shapeButton.checked && points.length > 6){
			var temp = points.copy();

			if(!touch){
				temp.pop();
				temp.pop();
				temp.pop();
			}

			dispatchEvent(new ModuleEvent(MEASUREMENTS_SHAPE, temp, [currentDrawingId], dicomWorkspace.id, true, true));
		}

		if(closeHandle != null && closeHandle.drawing.event.type == MEASUREMENTS_SHAPE){
			Log.debug('clicked handle nr ${closeHandle.index}');

			var points:Array<Float> = closeHandle.drawing.points.copy();
			points.splice(closeHandle.index, 3);

			if(points.length > 4)
				dispatchEvent(new ModuleEvent(MEASUREMENTS_SHAPE, points, [closeHandle.drawing.id], dicomWorkspace.id, true, true));
			else{
				drawings.remove(closeHandle.drawing.id);
				MessageHandler.externalMeasurements.remove(closeHandle.drawing.id);
				MessageHandler.send(new MeasurementData(MEASUREMENTS_DELETE, dicomWorkspace.dicomImageData.id, [], closeHandle.drawing.id));
				redraw();
			}
		}
		else if(closeHandle != null){
			Log.debug('clicked other handle nr ${closeHandle.index} - closing!');

			drawings.remove(closeHandle.drawing.id);
			MessageHandler.externalMeasurements.remove(closeHandle.drawing.id);
			MessageHandler.send(new MeasurementData(MEASUREMENTS_DELETE, dicomWorkspace.dicomImageData.id, [], closeHandle.drawing.id));
			redraw();
			points = [];
		}
		else
			points = [];
	}

	private function tryLineRelated(temp:Array<Float>, editable:Bool):Bool{
		if(temp.length == 6 && lineRelated() != null){
			if(lineRelated() == MEASUREMENTS_RECTANGLE || lineRelated() == MEASUREMENTS_ELLIPSIS){
				var local:Array<Float> = volumeToLocalFloats(temp);

				// top left, top right, bottom right, bottom left
				temp = [local[0], local[1], local[2], local[1], local[2], local[3], local[0], local[3]];
				temp = localToVolumeFloats(temp);
			}

			dispatchEvent(new ModuleEvent(lineRelated(), temp, [currentDrawingId], dicomWorkspace.id, editable, true));

			return true;
		}

		return false;
	}

	private function hideHistogram(){
		histogramContainer.classList.remove("show");
		histogramContainer.classList.remove("mouseEnabled");

		removeDrawing(PROFILE_DRAWING_ID);
	}

	private function updateHistogram(start:Vector2D, stop:Vector2D, ?scalar:Float){
		if(dicomWorkspace.dicomImage.pixels == null) // do not show on non-full res images - they dont have exact pixel data
			return;

		histogramStart = start;
		histogramStop = stop;

		histogramContainer.style.display = "block";
		histogramContainer.classList.add("show");
		hide(histogramMarker);
		hide(sectionValue);
		removeDrawing(PROFILE_DRAWING_ID);

		var histogramWidth:Float = histogramContainer.clientWidth;
		var histogramHeight:Float = histogramContainer.clientHeight;

		var diff:Vector2D = stop.subtract(start);

		var length:Float = diff.length();

		if(Math.abs(diff.x) > Math.abs(diff.y)){
			diff.y /= Math.abs(diff.x);
			diff.x /= Math.abs(diff.x);
		}
		else{
			diff.x /= Math.abs(diff.y);
			diff.y /= Math.abs(diff.y);
		}

		var stepLength:Float = diff.length();
		var steps:UInt = Math.floor(length / stepLength);
		var values:Array<Float> = [];
		var exists:Array<Bool> = [];

		var point:Vector2D = start.clone();

		var min:Float = 0;
		var max:Float = 0;

		for(n in 0...steps){
			if(point.x >= 0 && point.x < dicomWorkspace.dicomImage.width() && point.y >= 0 && point.y < dicomWorkspace.dicomImage.height()){
				var value:Float = dicomWorkspace.dicomImage.getPixel(Std.int(point.x), Std.int(point.y));

				min = max = value;

				values.push(value);
				exists.push(true);
			}
			else{
				values.push(0);
				exists.push(false);
			}

			point = point.add(diff);
		}

		for(n in 0...values.length)
			if(exists[n]){
				if(values[n] > max)
					max = values[n];

				if(values[n] < min)
					min = values[n];
			}

		var stepWidth:Float = histogramWidth / values.length;
		var valuesDiff:Float = max - min;

		var path:String = "";

		for(n in 0...values.length){
			if(!exists[n])
				continue;

			var y:Float = histogramHeight - histogramHeight * (values[n] - min) / valuesDiff;

			if(n == 0 || !exists[n - 1])
				path += 'M ${n * stepWidth} ${y} ';
			else
				path += 'L ${n * stepWidth} ${y} ';
		}

		if(path.indexOf("NaN") == -1)
			histogramPath.setAttribute("d", path);

		sectionMin.innerHTML = '${MathStats.roundTo(min, 1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';
		sectionMax.innerHTML = '${MathStats.roundTo(max, 1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';

		if(scalar == null)
			return;

		var diff:Vector2D = stop.subtract(start);

		var temp:Vector2D = diff.clone();

		temp.normalize();
		temp.scale(scalar * diff.length());

		var profile:Vector2D = start.add(temp);

		var perpendicular:Vector2D = new Vector2D(-diff.y, diff.x);
		perpendicular.normalize();
		perpendicular.scale(10 / dicomWorkspace.dicomImage.columnSpacing);

		var index:UInt = Math.floor(scalar * values.length);

		exists[index] ? show(sectionValue) : hide(sectionValue);
		sectionValue.innerHTML = '${MathStats.roundTo(values[index], 1)} ${DicomUtils.getVoxelUnit(dicomWorkspace.dicomImageData)}';
		var valuesDiff:Float = max - min;
		sectionValue.style.left = '${histogramWidth * scalar}px';
		sectionValue.style.top = '${histogramHeight - histogramHeight * (values[index] - min) / valuesDiff}px';

		show(histogramMarker);
		show(sectionValue);
		histogramMarker.style.left = '${histogramWidth * scalar}px';

		var imageStart = new Vector2D(profile.x - perpendicular.x, profile.y - perpendicular.y);
		var imageStop = new Vector2D(profile.x + perpendicular.x, profile.y + perpendicular.y);

		addDrawing(new Drawing(Drawing.SHAPE, pointsToFloats3D(imageToVolume([imageStart, imageStop])), PROFILE_DRAWING_ID, PROFILE_DRAWING_COLOR));
	}

	private function onOverHistogram(event:MouseEvent){
		event.stopImmediatePropagation();
		event.stopPropagation();

		if(histogramEvent == null)
			return;

		histogramEvent.scalar = (event.pageX - histogramContainer.offsetLeft + histogramContainer.clientWidth / 2) / histogramContainer.clientWidth;

		dispatchEvent(histogramEvent);
	}

	private function updateRulers(){
		while(rulersSvg.firstChild != null)
			rulersSvg.removeChild(rulersSvg.firstChild);

		hide(rulersContainer);

		if(enableRulers){
			show(rulersContainer);

			var margin:Float = 32;
			var marginX:Float = viewElement.clientWidth / 4;
			var marginY:Float = viewElement.clientHeight / 4;

			var horizontalRuler:PathElement = MeasurementUtils.drawMeasuredLine(marginX, viewElement.clientHeight - margin, viewElement.clientWidth - marginX, viewElement.clientHeight - margin, true, 10 );

			horizontalRuler.setAttribute("stroke", "#ffffff");
			horizontalRuler.setAttribute("stroke-width", "2");

			rulersSvg.appendChild(horizontalRuler);

			var verticalRuler:PathElement = MeasurementUtils.drawMeasuredLine(viewElement.clientWidth - margin, marginY, viewElement.clientWidth - margin, viewElement.clientHeight - marginY, true, 10 );

			verticalRuler.setAttribute("stroke", "#ffffff");
			verticalRuler.setAttribute("stroke-width", "2");

			rulersSvg.appendChild(verticalRuler);

			var length:Float = (viewElement.clientWidth - 2 * marginX) / dicomWorkspace.dicomImage.scale ;
			rulerBottom.innerHTML = MathStats.formatLengthInMm(length);
			rulerBottom.style.left = '${viewElement.clientWidth- marginX + 4}px';
			rulerBottom.style.top = '${viewElement.clientHeight - margin - 0.5 * rulerBottom.clientHeight}px';

			length = (viewElement.clientHeight - 2 * marginY) / dicomWorkspace.dicomImage.scale ;
			rulerRight.innerHTML = MathStats.formatLengthInMm(length);
			rulerRight.style.right = '0px';
			rulerRight.style.top = '${viewElement.clientHeight - marginY + 4}px';
		}
	}

	private function showComponents(components:Array<Element>, measurementInfo:MeasurementInfo){
		hideInfo(measurementInfo);

		for(component in components)
			component.style.display = "";
	}

	private function hideInfo(measurementInfo:MeasurementInfo){
		for(component in measurementInfo.viewElement.children)
			component.style.display = "none";
	}

	private function drawingId(type:String):String{
		return 'drawing-${type}-${Date.now().getTime()}-${Math.random()}';
	}
}
